import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { useGetRole } from 'hooks/ui/useGetRole';
import '@silevis/reactgrid/styles.css';
import ReactGridForm from './ReactGridForm';
import PreviewTable from './PreviewTable';
import { INVESTOR_UPLOAD } from 'adminConstants/routes';
import { useFunds } from 'hooks/data/fund/useFunds';
import { useAdvisersLeanResponse } from 'hooks/data/adviser/useAdvisers';
import { styled } from '@mui/material/styles';
import { useListFirmBankDetails } from 'hooks/data/firm/useFirmBankDetails';
import { firmBankDetailsDisplay } from 'further-ui/utils';
import { FirmSelector } from 'components/FirmSelector';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import PageContent from 'components/PageContent';
import Heading from 'components/Heading';
import api from 'lib/trpcClient';
import UploadStatus from './UploadStatus';
import type { UploadPreviewState, UploadSaveResultData } from './types';
import { InvestmentPaymentType } from 'further-types/payment';
import {
  getUploadModeFromPermissions,
  UploadMode,
} from 'further-types/investor-investment-upload';
import {
  createInitialGridData,
  GRID_DATA_STORAGE_KEY,
  GridData,
} from './helpers/gridHelpers';
import { useLocalStorage } from 'usehooks-ts';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { SortOrder } from 'further-types/utils';

const STEPS = {
  CREATE_GRID_DATA: 0,
  PREVIEW_UPLOAD: 1,
  UPLOAD_STATUS: 2,
};

const UPLOAD_STATE_STORAGE_KEY = 'investorUploadV2State';

const UPLOAD_ID_PARAM = 'uploadId';
const FIRM_ID_PARAM = 'firmId';

const isValidMongoId = (value: string | null): value is string =>
  typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

const getStatusParamsFromSearch = (
  search: string,
): { uploadId: string; firmId: string } | null => {
  const params = new URLSearchParams(search);
  const uploadId = params.get(UPLOAD_ID_PARAM);
  const firmId = params.get(FIRM_ID_PARAM);

  if (isValidMongoId(uploadId) && isValidMongoId(firmId)) {
    return { uploadId, firmId };
  }
  return null;
};

const buildStatusUrl = (uploadId: string, firmId: string) =>
  `${INVESTOR_UPLOAD}?${UPLOAD_ID_PARAM}=${encodeURIComponent(
    uploadId,
  )}&${FIRM_ID_PARAM}=${encodeURIComponent(firmId)}`;

type StoredUploadState = {
  activeStep: number;
  uploadId: string | null;
  firmId: string | null;
};

const loadStoredUploadState = (): StoredUploadState | null => {
  const stored = sessionStorage.getItem(UPLOAD_STATE_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (
      typeof parsed?.activeStep !== 'number' ||
      (parsed.uploadId !== null && typeof parsed.uploadId !== 'string') ||
      (parsed.firmId !== null && typeof parsed.firmId !== 'string')
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const saveStoredUploadState = (state: StoredUploadState) => {
  sessionStorage.setItem(UPLOAD_STATE_STORAGE_KEY, JSON.stringify(state));
};

const clearStoredUploadState = () => {
  sessionStorage.removeItem(UPLOAD_STATE_STORAGE_KEY);
};

const PAGE_HEADING = 'Investors: Upload investors and subscriptions';

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PageContainer heading={PAGE_HEADING}>{children}</PageContainer>
);

const FirmSelectorWrapper = styled('div')({
  marginBottom: 24,
  maxWidth: '20rem',
});

const Paragraph = styled('div')({
  marginBottom: 24,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '82rem',
  gap: 8,
});

const UploadInvestorInvestment: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [gridDropdownsData, setGridDropdownsData] = useState<
    Record<string, undefined | Array<{ label: string; value: string }>>
  >({
    tranche: [],
    adviser: [],
    additional_linked_adviser: [],
    new_investor_legal_entity_adviser: [],
    new_investor_adviser: [],
    paid_to: [],
  });
  const { firmId: usersFirmId, isSuperAdmin } = useGetRole();
  const [activeStep, setActiveStep] = useState(STEPS.CREATE_GRID_DATA);
  const [uploadPreviewState, setUploadPreviewState] =
    useState<UploadPreviewState>({
      totalInvestors: 0,
      existingInvestorAccounts: [],
    });
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [uploadStatusSeed, setUploadStatusSeed] =
    useState<UploadSaveResultData | null>(null);
  const [firmId, setFirmId] = useState(usersFirmId);
  const [, setGridData] = useLocalStorage<GridData>(
    GRID_DATA_STORAGE_KEY,
    createInitialGridData(),
  );
  const firmIdChangeDisclosure = useDisclosure();

  const { profile } = useCurrentUser();
  const uploadMode =
    profile.data?.permissions != null
      ? getUploadModeFromPermissions(profile.data.permissions)
      : undefined;

  const { tranches } = useFunds({
    firmId,
    fullyDeployed: false,
    disabled:
      !firmId || uploadMode === undefined || uploadMode === UploadMode.Basic,
  });

  const { data: openTranchesForUpload } =
    api.investorInvestmentUpload.getOpenTranchesForUpload.useQuery(undefined, {
      enabled: uploadMode === UploadMode.Basic && !!firmId,
    });

  const { data: coreFeeTemplates } = api.coreFeeTemplates.list.useQuery(
    {
      firmId,
      order: SortOrder.Desc,
      orderBy: 'createdAt',
    },
    { enabled: uploadMode === UploadMode.Full && !!firmId },
  );

  const { data: performanceFeeTemplates } =
    api.performanceFeeTemplates.list.useQuery(
      {
        firmId,
        order: SortOrder.Desc,
        orderBy: 'createdAt',
      },
      { enabled: uploadMode === UploadMode.Full && !!firmId },
    );

  const { data } = useAdvisersLeanResponse(
    {
      firmId,
      order: SortOrder.Asc,
      orderBy: 'fullName',
    },
    undefined,
    {
      enabled: uploadMode === UploadMode.Full && !!firmId,
    },
  );

  const firmBankDetails = useListFirmBankDetails(firmId, undefined, {
    enabled: uploadMode === UploadMode.Full,
  });

  useEffect(() => {
    const fromUrl = getStatusParamsFromSearch(location.search);

    if (fromUrl) {
      setUploadId(fromUrl.uploadId);
      setFirmId(fromUrl.firmId);
      setActiveStep(STEPS.UPLOAD_STATUS);
      return;
    }
    const stored = loadStoredUploadState();
    if (stored?.activeStep === STEPS.UPLOAD_STATUS && stored.uploadId) {
      setUploadId(stored.uploadId);
      if (stored.firmId) {
        setFirmId(stored.firmId);
      }
      setActiveStep(STEPS.UPLOAD_STATUS);
    }
  }, []);

  useEffect(() => {
    if (activeStep === STEPS.CREATE_GRID_DATA && !uploadId) {
      clearStoredUploadState();
      return;
    }
    saveStoredUploadState({ activeStep, uploadId, firmId: firmId ?? null });
  }, [activeStep, uploadId, firmId]);

  useEffect(() => {
    if (
      activeStep === STEPS.UPLOAD_STATUS &&
      uploadId &&
      firmId &&
      !getStatusParamsFromSearch(location.search)
    ) {
      history.replace(buildStatusUrl(uploadId, firmId));
    }
  }, [activeStep, uploadId, firmId, history, location.search]);

  useEffect(
    function setDropdownListsData() {
      if (uploadMode === undefined) {
        return;
      }
      if (uploadMode === UploadMode.Basic) {
        const tranchesList = openTranchesForUpload ?? [];
        setGridDropdownsData((gridDropdownsData) => ({
          ...gridDropdownsData,
          tranche: tranchesList,
          adviser: [],
          additional_linked_adviser: [],
          new_investor_legal_entity_adviser: [],
          new_investor_adviser: [],
          paid_to: [],
          core_fee_template_id: [],
          performance_fee_template_id: [],
        }));
        return;
      }

      if (tranches.data && data?.advisers) {
        const tranchesList = tranches.data.map(
          ({ _id, fundName, firmId: { firmName } }) => ({
            label: `${firmName} ${fundName}`,
            value: _id,
          }),
        );

        const advisersList = data.advisers.map(
          ({ _id, fullName, email, organisation }) => {
            const name = fullName ? `${fullName} (${email})` : email;
            const org = organisation?.name ? ` [${organisation.name}]` : '';
            return {
              label: `${name}${org}`,
              value: _id,
            };
          },
        );

        const bankDetailsList = firmBankDetails?.data?.map((bankDetails) => ({
          label: firmBankDetailsDisplay(bankDetails),
          value: bankDetails._id,
        }));

        const listSorter = ({ label: labelA }, { label: labelB }) =>
          labelA.localeCompare(labelB);

        tranchesList.sort(listSorter);
        advisersList.sort(listSorter);
        if (bankDetailsList?.length) bankDetailsList.sort(listSorter);

        setGridDropdownsData((gridDropdownsData) => ({
          ...gridDropdownsData,
          tranche: tranchesList,
          adviser: advisersList,
          additional_linked_adviser: advisersList,
          new_investor_legal_entity_adviser: advisersList,
          new_investor_adviser: advisersList,
          paid_to: [
            {
              label: 'Cash balance payment',
              value: InvestmentPaymentType.CashBalance,
            },
            ...(bankDetailsList ?? []),
          ],
          core_fee_template_id: coreFeeTemplates?.result.map(
            ({ _id, name }) => ({
              label: name,
              value: _id,
            }),
          ),
          performance_fee_template_id: performanceFeeTemplates?.result.map(
            ({ _id, name }) => ({
              label: name,
              value: _id,
            }),
          ),
        }));
      }
    },
    [
      uploadMode,
      openTranchesForUpload,
      tranches.data,
      data?.advisers,
      firmBankDetails?.data,
      coreFeeTemplates,
      performanceFeeTemplates,
    ],
  );

  const onFirmChange = (newFirmId: string) => {
    if (firmId && firmId !== newFirmId) {
      firmIdChangeDisclosure.stageAction({ newFirmId });
    } else {
      setFirmId(newFirmId);
    }
  };

  const onFirmChangeConfirm = () => {
    setFirmId(firmIdChangeDisclosure.actionProps.newFirmId);
    firmIdChangeDisclosure.onClose();
  };

  const uploadPreviewSet = (uploadPreviewState: UploadPreviewState) => {
    setUploadId(null);
    setUploadStatusSeed(null);
    setUploadPreviewState(uploadPreviewState);
    setActiveStep(STEPS.PREVIEW_UPLOAD);
  };

  const uploadStatusSet = (uploadResult: UploadSaveResultData) => {
    // Clear grid data on submission so returning to this page later starts fresh
    setGridData(createInitialGridData());

    setUploadId(uploadResult.uploadId);
    setUploadStatusSeed(uploadResult);
    setActiveStep(STEPS.UPLOAD_STATUS);
  };

  const handleRestart = () => {
    setGridData(createInitialGridData());
    clearStoredUploadState();
    setUploadId(null);
    setUploadStatusSeed(null);
    setActiveStep(STEPS.CREATE_GRID_DATA);
    history.replace(INVESTOR_UPLOAD);
  };

  if (activeStep === STEPS.CREATE_GRID_DATA)
    return (
      <Wrapper>
        <PageContent>
          {isSuperAdmin && (
            <FirmSelectorWrapper>
              <FirmSelector
                onChange={onFirmChange}
                firmId={firmId}
                placeholder="Select a firm"
              />
            </FirmSelectorWrapper>
          )}
          {firmId && uploadMode === undefined && (
            <>
              <Heading variant="h2">
                Input your investors and subscriptions into the table
              </Heading>
              <Paragraph>
                <p>Loading…</p>
              </Paragraph>
            </>
          )}
          {firmId && uploadMode !== undefined && (
            <>
              <Heading variant="h2">
                Input your investors and subscriptions into the table
              </Heading>
              <Paragraph>
                <p>
                  <b>How to use this feature:</b> The below upload table can be
                  used for uploading one or more investors and subscriptions.
                  Type or paste information into cells to continue. Leaving this
                  page without submitting can cause entered data to be lost.
                  Rows are matched to existing investors by name and identity
                  date; subscriptions will be added to the relevant existing
                  investor&apos;s account.
                </p>
              </Paragraph>
              <ReactGridForm
                gridDropdownsData={gridDropdownsData}
                uploadPreviewSet={uploadPreviewSet}
                firmId={firmId}
                uploadMode={uploadMode}
              />
            </>
          )}
        </PageContent>
        <AlertDialog
          open={firmIdChangeDisclosure.isOpen}
          onClose={firmIdChangeDisclosure.onClose}
          onConfirm={onFirmChangeConfirm}
          title="Change firm?"
          content="This will reset the values in the tranche, adviser and paid to dropdowns."
        />
      </Wrapper>
    );

  if (activeStep === STEPS.PREVIEW_UPLOAD)
    return (
      <Wrapper>
        <PageContent>
          {uploadMode === undefined ? (
            <Paragraph>
              <p>Loading…</p>
            </Paragraph>
          ) : (
            <PreviewTable
              totalInvestors={uploadPreviewState.totalInvestors}
              existingInvestorAccounts={
                uploadPreviewState.existingInvestorAccounts
              }
              handleSubmitCallback={uploadStatusSet}
              handleBack={() => setActiveStep(STEPS.CREATE_GRID_DATA)}
              tranches={
                uploadMode === UploadMode.Basic
                  ? (openTranchesForUpload ?? []).map(({ value, label }) => ({
                      _id: value,
                      fundName: label,
                    }))
                  : tranches?.data ?? []
              }
              firmId={firmId}
              gridDropdownsData={gridDropdownsData}
              uploadMode={uploadMode}
            />
          )}
        </PageContent>
      </Wrapper>
    );

  if (activeStep === STEPS.UPLOAD_STATUS && uploadId && firmId) {
    return (
      <Wrapper>
        <UploadStatus
          uploadId={uploadId}
          firmId={firmId}
          uploadMode={uploadMode}
          initialStatus={uploadStatusSeed ?? undefined}
          onRestart={handleRestart}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <PageContent>
        {activeStep === STEPS.UPLOAD_STATUS ? (
          <>
            <Heading variant="h2">Upload status</Heading>
            <p>Loading upload status...</p>
          </>
        ) : (
          <>
            <Heading variant="h2">Upload status</Heading>
            <p>
              We could not load the upload status. Please return to the upload
              screen to start a new upload.
            </p>
            <p>
              <a href={INVESTOR_UPLOAD}>Back to upload</a>
            </p>
          </>
        )}
      </PageContent>
    </Wrapper>
  );
};

export default UploadInvestorInvestment;
